/**
 * reactbits-init.js — Mango Butter Brand
 * Ports key ReactBits components as React islands + vanilla fallbacks.
 * Mounts via data-reactbits attribute on any element in any section.
 *
 * ── HOW TO USE IN A SECTION ──────────────────────────────────────────────
 *
 * 1. Blur Text (hero headings):
 *    <h1 data-reactbits="BlurText"
 *        data-text="Reveal Your Glow"
 *        data-delay="80">
 *    </h1>
 *
 * 2. Split Text (dramatic word entrance):
 *    <p data-reactbits="SplitText"
 *       data-text="The routine is over."
 *       data-delay="40">
 *    </p>
 *
 * 3. Shiny Text (product names, badges):
 *    <span data-reactbits="ShinyText"
 *          data-text="The Glow Butter">
 *    </span>
 *
 * 4. Gradient Text (brand accent):
 *    <span data-reactbits="GradientText"
 *          data-text="Reveal Your Glow">
 *    </span>
 *
 * 5. Rotating Text (hero subheading cycles):
 *    <span data-reactbits="RotatingText"
 *          data-words='["moisturize","bond","glow","last"]'
 *          data-prefix="Made to ">
 *    </span>
 *
 * 6. Count Up (social proof numbers):
 *    <span data-reactbits="CountUp"
 *          data-to="200"
 *          data-suffix="+"
 *          data-prefix=""
 *          data-duration="2000">
 *    </span>
 *
 * 7. Aurora background (sections):
 *    <div data-reactbits="Aurora" style="position:relative; height:600px;">
 *      <div style="position:relative; z-index:1;">Your content</div>
 *    </div>
 *
 * 8. Particles background:
 *    <div data-reactbits="Particles"
 *         data-count="30"
 *         style="position:relative; height:400px;">
 *      <div style="position:relative; z-index:1;">Your content</div>
 *    </div>
 *
 * 9. Spotlight (hero sections — follows cursor):
 *    <div data-reactbits="Spotlight" style="position:relative;">
 *      Your content
 *    </div>
 *
 * 10. Tilt Card (product cards):
 *     <div data-reactbits="TiltCard">Your card content</div>
 *
 * 11. Magnetic Button (CTA buttons):
 *     <button data-reactbits="MagneticButton">Shop Now</button>
 *
 * 12. Fade In (any element):
 *     <div data-reactbits="FadeIn"
 *          data-direction="up"
 *          data-delay="200">
 *       Your content
 *     </div>
 *
 * 13. Stagger children:
 *     <div data-reactbits="Stagger">
 *       <div>Item 1</div>
 *       <div>Item 2</div>
 *       <div>Item 3</div>
 *     </div>
 *
 * 14. Glass Card:
 *     <div data-reactbits="GlassCard">Your content</div>
 *
 * 15. Border Beam (animated glowing border):
 *     <div data-reactbits="BorderBeam">Your content</div>
 *
 * 16. Pixel Trail (cursor effect — applied to entire page):
 *     <div data-reactbits="PixelTrail"></div>
 *
 * ─────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ── Wait for React + ReactDOM ────────────────────────────────────────────
  function waitForReact(cb) {
    if (typeof React !== 'undefined' && typeof ReactDOM !== 'undefined') {
      cb();
    } else {
      setTimeout(() => waitForReact(cb), 50);
    }
  }

  // ── Reduced motion check ─────────────────────────────────────────────────
  const noMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── Intersection Observer helper ─────────────────────────────────────────
  function onVisible(el, cb, threshold = 0.1) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            cb(e.target);
            io.unobserve(e.target);
          }
        });
      },
      { threshold }
    );
    io.observe(el);
    return io;
  }

  // ── Stagger delay helper ─────────────────────────────────────────────────
  function applyStaggerDelay(parent, baseDelay = 0, interval = 80) {
    [...parent.children].forEach((child, i) => {
      child.style.transitionDelay = `${baseDelay + i * interval}ms`;
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // VANILLA JS COMPONENTS
  // (run immediately — no React required)
  // ═══════════════════════════════════════════════════════════════════════

  function initVanilla() {

    // ── BlurText ────────────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="BlurText"]').forEach((el) => {
      const text  = el.dataset.text || el.textContent.trim();
      const delay = parseInt(el.dataset.delay ?? 80, 10);

      if (noMotion) { el.textContent = text; return; }

      el.textContent = '';
      const words = text.split(' ');
      words.forEach((word, i) => {
        const span = document.createElement('span');
        span.className = 'rb-blur-word';
        span.textContent = word + (i < words.length - 1 ? '\u00A0' : '');
        span.style.transitionDelay = `${i * delay}ms`;
        el.appendChild(span);
      });

      onVisible(el, () => {
        el.querySelectorAll('.rb-blur-word').forEach(s => s.classList.add('rb-visible'));
      }, 0.2);
    });

    // ── SplitText ───────────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="SplitText"]').forEach((el) => {
      const text  = el.dataset.text || el.textContent.trim();
      const delay = parseInt(el.dataset.delay ?? 40, 10);

      if (noMotion) { el.textContent = text; return; }

      el.textContent = '';
      [...text].forEach((char, i) => {
        const span = document.createElement('span');
        span.className = 'rb-split-char';
        span.textContent = char === ' ' ? '\u00A0' : char;
        span.style.transitionDelay = `${i * delay}ms`;
        el.appendChild(span);
      });

      onVisible(el, () => {
        el.querySelectorAll('.rb-split-char').forEach(s => s.classList.add('rb-visible'));
      }, 0.2);
    });

    // ── ShinyText ───────────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="ShinyText"]').forEach((el) => {
      const text = el.dataset.text || el.textContent.trim();
      el.textContent = text;
      el.classList.add('rb-shiny-text');
      if (!noMotion) el.classList.add('rb-animate');
    });

    // ── GradientText ────────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="GradientText"]').forEach((el) => {
      const text = el.dataset.text || el.textContent.trim();
      el.textContent = text;
      el.classList.add('rb-gradient-text');
    });

    // ── RotatingText ────────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="RotatingText"]').forEach((el) => {
      let words;
      try { words = JSON.parse(el.dataset.words || '["glow","bond","hydrate"]'); }
      catch { words = ['glow', 'bond', 'hydrate']; }

      const prefix   = el.dataset.prefix || '';
      const interval = parseInt(el.dataset.interval ?? 2200, 10);

      if (noMotion) { el.textContent = prefix + words[0]; return; }

      const wrapper = document.createElement('span');
      wrapper.style.cssText = 'display:inline-flex;align-items:center;overflow:hidden;vertical-align:bottom;';

      if (prefix) {
        const pre = document.createElement('span');
        pre.textContent = prefix;
        wrapper.appendChild(pre);
      }

      const wordWrap = document.createElement('span');
      wordWrap.style.cssText = 'position:relative;display:inline-flex;min-width:80px;';

      words.forEach((word, i) => {
        const span = document.createElement('span');
        span.className = 'rb-rotating-word' + (i === 0 ? ' rb-active' : '');
        span.textContent = word;
        span.style.color = '#D87D4A';
        wordWrap.appendChild(span);
      });

      wrapper.appendChild(wordWrap);
      el.textContent = '';
      el.appendChild(wrapper);

      let current = 0;
      setInterval(() => {
        const all = wordWrap.querySelectorAll('.rb-rotating-word');
        all[current].classList.remove('rb-active');
        current = (current + 1) % words.length;
        all[current].classList.add('rb-active');
      }, interval);
    });

    // ── CountUp ─────────────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="CountUp"]').forEach((el) => {
      const to       = parseFloat(el.dataset.to   ?? el.textContent ?? 100);
      const from     = parseFloat(el.dataset.from  ?? 0);
      const dur      = parseInt(el.dataset.duration ?? 2000, 10);
      const prefix   = el.dataset.prefix  ?? '';
      const suffix   = el.dataset.suffix  ?? '';
      const decimals = parseInt(el.dataset.decimals ?? 0, 10);

      el.classList.add('rb-count-number');
      el.textContent = prefix + from.toFixed(decimals) + suffix;

      if (noMotion) { el.textContent = prefix + to.toFixed(decimals) + suffix; return; }

      onVisible(el, () => {
        const start = performance.now();
        function tick(now) {
          const t = Math.min((now - start) / dur, 1);
          const eased = 1 - Math.pow(1 - t, 3);
          const val = from + (to - from) * eased;
          el.textContent = prefix + val.toFixed(decimals) + suffix;
          if (t < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      });
    });

    // ── FadeIn ──────────────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="FadeIn"]').forEach((el) => {
      if (noMotion) return;
      const delay = parseInt(el.dataset.delay ?? 0, 10);
      const dir   = el.dataset.direction || 'up';
      el.classList.add('rb-fade-in');
      el.setAttribute('data-direction', dir);
      el.style.transitionDelay = `${delay}ms`;
      onVisible(el, () => el.classList.add('rb-visible'));
    });

    // ── Stagger ─────────────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="Stagger"]').forEach((el) => {
      if (noMotion) return;
      const interval = parseInt(el.dataset.interval ?? 80, 10);
      const delay    = parseInt(el.dataset.delay    ?? 0,  10);
      el.classList.add('rb-stagger');
      applyStaggerDelay(el, delay, interval);
      onVisible(el, () => el.classList.add('rb-visible'), 0.1);
    });

    // ── GlassCard ───────────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="GlassCard"]').forEach((el) => {
      el.classList.add('rb-glass-card');
    });

    // ── BorderBeam ──────────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="BorderBeam"]').forEach((el) => {
      if (noMotion) return;
      el.classList.add('rb-border-beam');
    });

    // ── TiltCard ────────────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="TiltCard"]').forEach((el) => {
      if (noMotion) return;
      el.classList.add('rb-tilt');

      const maxTilt = parseFloat(el.dataset.maxTilt ?? 12);

      el.addEventListener('mousemove', (e) => {
        const rect   = el.getBoundingClientRect();
        const cx     = rect.left + rect.width  / 2;
        const cy     = rect.top  + rect.height / 2;
        const dx     = (e.clientX - cx) / (rect.width  / 2);
        const dy     = (e.clientY - cy) / (rect.height / 2);
        const rotateX = -dy * maxTilt;
        const rotateY =  dx * maxTilt;
        el.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;
      });

      el.addEventListener('mouseleave', () => {
        el.style.transform = 'perspective(800px) rotateX(0deg) rotateY(0deg) scale(1)';
      });
    });

    // ── MagneticButton ──────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="MagneticButton"]').forEach((el) => {
      if (noMotion) return;
      el.classList.add('rb-magnetic');
      const strength = parseFloat(el.dataset.strength ?? 0.4);

      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        const cx   = rect.left + rect.width  / 2;
        const cy   = rect.top  + rect.height / 2;
        const dx   = (e.clientX - cx) * strength;
        const dy   = (e.clientY - cy) * strength;
        el.style.transform = `translate(${dx}px, ${dy}px)`;
      });

      el.addEventListener('mouseleave', () => {
        el.style.transform = 'translate(0, 0)';
      });
    });

    // ── Aurora Background ────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="Aurora"]').forEach((el) => {
      if (noMotion) return;
      if (el.style.position !== 'relative' && el.style.position !== 'absolute') {
        el.style.position = 'relative';
      }
      const aurora = document.createElement('div');
      aurora.className = 'rb-aurora';
      aurora.innerHTML = '<div class="rb-aurora-blob"></div><div class="rb-aurora-blob"></div><div class="rb-aurora-blob"></div>';
      el.insertBefore(aurora, el.firstChild);
    });

    // ── Particles ────────────────────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="Particles"]').forEach((el) => {
      if (noMotion) return;
      const count = parseInt(el.dataset.count ?? 25, 10);
      const container = document.createElement('div');
      container.className = 'rb-particles';

      for (let i = 0; i < count; i++) {
        const dot = document.createElement('div');
        dot.className = 'rb-particle';
        const size = Math.random() * 4 + 2;
        dot.style.cssText = `
          width:${size}px;
          height:${size}px;
          left:${Math.random() * 100}%;
          animation-duration:${Math.random() * 8 + 6}s;
          animation-delay:${Math.random() * -10}s;
          opacity:${Math.random() * 0.4 + 0.1};
        `;
        container.appendChild(dot);
      }

      if (el.style.position !== 'relative' && el.style.position !== 'absolute') {
        el.style.position = 'relative';
      }
      el.insertBefore(container, el.firstChild);
    });

    // ── Spotlight (cursor glow) ───────────────────────────────────────────
    document.querySelectorAll('[data-reactbits="Spotlight"]').forEach((el) => {
      if (noMotion || window.matchMedia('(pointer: coarse)').matches) return;

      const spotlight = document.createElement('div');
      spotlight.className = 'rb-spotlight';
      const beam = document.createElement('div');
      beam.className = 'rb-spotlight-beam';
      spotlight.appendChild(beam);

      if (el.style.position !== 'relative' && el.style.position !== 'absolute') {
        el.style.position = 'relative';
      }
      el.insertBefore(spotlight, el.firstChild);

      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        beam.style.left = `${e.clientX - rect.left}px`;
        beam.style.top  = `${e.clientY - rect.top}px`;
        beam.style.opacity = '1';
      });

      el.addEventListener('mouseleave', () => {
        beam.style.opacity = '0';
      });
    });

    // ── PixelTrail (full-page cursor trail) ───────────────────────────────
    document.querySelectorAll('[data-reactbits="PixelTrail"]').forEach(() => {
      if (noMotion || window.matchMedia('(pointer: coarse)').matches) return;

      const trailCount = 8;
      const dots = [];

      for (let i = 0; i < trailCount; i++) {
        const dot = document.createElement('div');
        dot.className = 'rb-trail-dot';
        const size = Math.max(4, 14 - i * 1.2);
        dot.style.cssText = `
          width:${size}px;
          height:${size}px;
          margin-left:-${size/2}px;
          margin-top:-${size/2}px;
          opacity:${(1 - i / trailCount) * 0.7};
        `;
        document.body.appendChild(dot);
        dots.push({ el: dot, x: -100, y: -100 });
      }

      let mouseX = -100, mouseY = -100;
      document.addEventListener('mousemove', (e) => {
        mouseX = e.clientX;
        mouseY = e.clientY;
        dots[0].el.style.opacity = String((1 - 0 / trailCount) * 0.7);
      });

      // Chain each dot toward the previous with a lag
      function animateTrail() {
        dots[0].x = mouseX;
        dots[0].y = mouseY;
        dots[0].el.style.left = mouseX + 'px';
        dots[0].el.style.top  = mouseY + 'px';

        for (let i = 1; i < dots.length; i++) {
          dots[i].x += (dots[i - 1].x - dots[i].x) * 0.35;
          dots[i].y += (dots[i - 1].y - dots[i].y) * 0.35;
          dots[i].el.style.left = dots[i].x + 'px';
          dots[i].el.style.top  = dots[i].y + 'px';
        }
        requestAnimationFrame(animateTrail);
      }
      animateTrail();
    });

  } // end initVanilla

  // ═══════════════════════════════════════════════════════════════════════
  // REACT ISLAND COMPONENTS
  // (richer components that benefit from React state)
  // ═══════════════════════════════════════════════════════════════════════

  function initReactIslands() {
    if (typeof React === 'undefined' || typeof ReactDOM === 'undefined') return;
    const { createElement: h, useState, useEffect, useRef } = React;

    // ── ScrollVelocity (marquee that reacts to scroll speed) ────────────
    document.querySelectorAll('[data-reactbits="ScrollVelocity"]').forEach((el) => {
      if (noMotion) return;

      const text  = el.dataset.text  || el.textContent.trim();
      const speed = parseFloat(el.dataset.speed ?? 1);

      function ScrollVelocity() {
        const trackRef = useRef(null);
        const xRef     = useRef(0);
        const velRef   = useRef(0);
        const lastScrollY = useRef(window.scrollY);

        useEffect(() => {
          let raf;
          function animate() {
            const scrollY    = window.scrollY;
            const delta      = scrollY - lastScrollY.current;
            lastScrollY.current = scrollY;

            velRef.current += delta * speed * 0.5;
            velRef.current *= 0.9; // damping

            xRef.current += velRef.current;

            // Reset when gone past half
            const track = trackRef.current;
            if (!track) { raf = requestAnimationFrame(animate); return; }
            const half = track.scrollWidth / 2;
            if (Math.abs(xRef.current) >= half) xRef.current = 0;

            track.style.transform = `translateX(${xRef.current}px)`;
            raf = requestAnimationFrame(animate);
          }
          raf = requestAnimationFrame(animate);
          return () => cancelAnimationFrame(raf);
        }, []);

        const items = `${text} · `.repeat(8);
        return h('div', { className: 'rb-scroll-velocity' },
          h('div', { className: 'rb-velocity-track', ref: trackRef },
            h('span', null, items),
            h('span', { 'aria-hidden': 'true' }, items)
          )
        );
      }

      el.textContent = '';
      const root = ReactDOM.createRoot(el);
      root.render(h(ScrollVelocity));
    });

  } // end initReactIslands

  // ═══════════════════════════════════════════════════════════════════════
  // BOOT
  // ═══════════════════════════════════════════════════════════════════════

  function boot() {
    initVanilla();
    waitForReact(initReactIslands);

    // Re-init on section reload (Shopify Theme Editor live preview)
    document.addEventListener('shopify:section:load', () => {
      initVanilla();
      waitForReact(initReactIslands);
    });

    // Expose on window.MB for manual use
    window.MB = window.MB || {};
    window.MB.reactbits = { reinit: initVanilla };

    document.dispatchEvent(new CustomEvent('MB:reactbits:ready', { bubbles: true }));
    console.log('[MB] ReactBits ready ✓');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
